import { create } from 'zustand';

/**
 * Tema claro/escuro (POC modo noturno). Persiste a preferência em localStorage
 * e aplica a classe `dark` no <html> — a inversão de tokens (globals.css `.dark`)
 * cuida do resto. O flash inicial é evitado por um script inline no index.html
 * que aplica a classe antes do React montar (lê a mesma chave).
 */
type ThemePref = 'light' | 'dark' | 'system';
const STORAGE_KEY = 'chatplus-theme';

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false;
}

function resolve(pref: ThemePref): 'light' | 'dark' {
  return pref === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : pref;
}

function applyToDom(pref: ThemePref): 'light' | 'dark' {
  const resolved = resolve(pref);
  if (typeof document !== 'undefined') {
    document.documentElement.classList.toggle('dark', resolved === 'dark');
  }
  return resolved;
}

function readInitial(): ThemePref {
  try {
    const s = localStorage.getItem(STORAGE_KEY);
    if (s === 'light' || s === 'dark' || s === 'system') return s;
  } catch {
    /* localStorage indisponível */
  }
  // Default = light (dark é opt-in pelo toggle). Não segue o SO por padrão pra
  // não surpreender atendentes acostumados com o tema claro.
  return 'light';
}

interface ThemeState {
  pref: ThemePref;
  resolved: 'light' | 'dark';
  setPref: (pref: ThemePref) => void;
  /** Alterna explicitamente entre claro e escuro (sai do modo `system`). */
  toggle: () => void;
}

const initialPref = readInitial();

export const useThemeStore = create<ThemeState>((set, get) => ({
  pref: initialPref,
  resolved: resolve(initialPref),
  setPref: (pref) => {
    try {
      localStorage.setItem(STORAGE_KEY, pref);
    } catch {
      /* ignore */
    }
    set({ pref, resolved: applyToDom(pref) });
  },
  toggle: () => {
    get().setPref(get().resolved === 'dark' ? 'light' : 'dark');
  },
}));
